import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Zap } from "lucide-react";
import { useRef } from "react";
import { useCart } from "@/context/CartContext";

const PRODUCT_IMG = "https://customer-assets.emergentagent.com/job_04829c98-cd55-43c7-9947-d6a9dd40b6ff/artifacts/yc6g90h6_IMG_5079.jpeg";

const Hero = () => {
  const { addItem } = useCart();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [10, -10]), { stiffness: 80, damping: 16 });
  const ry = useSpring(useTransform(mx, [-1, 1], [-12, 12]), { stiffness: 80, damping: 16 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const handleAdd = () => addItem({ quantity: 1 });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16"
      onMouseMove={onMouseMove}
      onMouseLeave={onLeave}
      ref={ref}
    >
      {/* Background grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      {/* Accent radial */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,122,255,0.18) 0%, transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center pt-12 pb-20 z-10">
        {/* Left: copy */}
        <div className="order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#007AFF]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#007AFF] font-body">
              GFX-04 · Series II
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.92]"
            data-testid="hero-headline"
          >
            BUILT FOR GRIP.<br/>
            ENGINEERED FOR<br/>
            <span className="text-[#007AFF]">DOMINANCE.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-8 max-w-md text-white/65 leading-relaxed text-base"
          >
            A spherical, six-axis tension trainer machined for athletes who refuse plateaus.
            Hand-tuned resistance. Brutalist form. Zero compromises.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={handleAdd}
              data-testid="hero-add-to-cart-button"
              className="cta-glow group relative inline-flex items-center gap-3 px-8 py-4 bg-[#007AFF] text-black font-body font-bold text-sm uppercase tracking-[0.25em] hover:bg-white hover:text-[#0a0a0a] transition-colors"
            >
              <Zap className="w-4 h-4" strokeWidth={2.5} />
              Add to Cart — $19.99
            </button>
            <a
              href="#how"
              className="inline-flex items-center gap-2 px-6 py-4 border border-white/15 hover:border-white/40 text-xs uppercase tracking-[0.25em] font-body transition"
              data-testid="hero-learn-more"
            >
              See How It Works
              <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-14 flex items-center gap-8 text-xs uppercase tracking-[0.2em] text-white/45 font-body"
          >
            <div><span className="text-white text-base font-heading block">40-99</span>LB RANGE</div>
            <div className="h-8 w-px bg-white/10" />
            <div><span className="text-white text-base font-heading block">5</span>RESISTANCE KNOBS</div>
            <div className="h-8 w-px bg-white/10" />
            <div><span className="text-white text-base font-heading block">II</span>SERIES</div>
          </motion.div>
        </div>

        {/* Right: product */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
          className="order-1 lg:order-2 relative flex items-center justify-center min-h-[420px] lg:min-h-[600px]"
          style={{ perspective: 1200 }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[420px] h-[420px] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(0,122,255,0.18) 0%, transparent 65%)", filter: "blur(20px)" }}
            />
          </div>

          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <div className="levitate">
              <img
                src={PRODUCT_IMG}
                alt="GripForce X grip strengthener"
                className="w-[320px] sm:w-[400px] lg:w-[480px] product-mask select-none"
                draggable={false}
                data-testid="hero-product-image"
              />
            </div>
          </motion.div>

          {/* Floating tag */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="absolute top-12 right-4 lg:right-12 border border-white/10 bg-black/60 backdrop-blur px-4 py-3 text-xs font-body"
          >
            <div className="text-[#007AFF] uppercase tracking-[0.2em] text-[10px] mb-1">Tension</div>
            <div className="font-heading text-xl">40–99 LB</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="absolute bottom-16 left-2 lg:left-8 border border-white/10 bg-black/60 backdrop-blur px-4 py-3 text-xs font-body"
          >
            <div className="text-[#007AFF] uppercase tracking-[0.2em] text-[10px] mb-1">Knobs</div>
            <div className="font-heading text-xl">5 / HEX</div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-white/35 font-body flex flex-col items-center gap-2"
      >
        <span>Scroll</span>
        <span className="h-10 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
